import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight, ChevronRight, Terminal } from "lucide-react";
import { CALENDAR, HERO_LINES, TERMINAL_LINES } from "./data";
import { EASE, Magnetic } from "./Reveal";

function useTypewriter(lines, speed = 15, pause = 210, startDelay = 950) {
  const [done, setDone] = useState([]);
  const [current, setCurrent] = useState("");
  useEffect(() => {
    let li = 0;
    let ci = 0;
    let timer;
    const tick = () => {
      if (li >= lines.length) return;
      const text = lines[li].text;
      if (ci < text.length) {
        ci += 1;
        setCurrent(text.slice(0, ci));
        timer = setTimeout(tick, speed);
      } else {
        const finished = lines[li];
        li += 1;
        ci = 0;
        setDone((d) => [...d, finished]);
        setCurrent("");
        timer = setTimeout(tick, pause);
      }
    };
    timer = setTimeout(tick, startDelay);
    return () => clearTimeout(timer);
  }, [lines, speed, pause, startDelay]);
  return { done, current, finished: done.length === lines.length && current === "" };
}

function TerminalLine({ line }) {
  if (line.tone === "cmd") {
    return (
      <p className="tone-cmd">
        <span className="cyan">$</span> {line.text.slice(2)}
      </p>
    );
  }
  return <p className={`tone-${line.tone}`}>{line.text}</p>;
}

function TerminalCard() {
  const { done, current, finished } = useTypewriter(TERMINAL_LINES);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [5, -5]), { stiffness: 130, damping: 17 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-7, 7]), { stiffness: 130, damping: 17 });

  return (
    <div
      className="tilt"
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
      }}
      onMouseLeave={() => {
        mx.set(0);
        my.set(0);
      }}
    >
      <motion.div
        className="terminal-card"
        style={{ rotateX, rotateY }}
        initial={{ opacity: 0, y: 34 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.55, ease: EASE }}
        data-testid="hero-terminal"
      >
        <div className="terminal-top">
          <span className="terminal-dots">
            <i />
            <i />
            <i />
          </span>
          <span className="terminal-title">
            <Terminal size={13} /> sandeep@builder ~ zsh
          </span>
          <span className="terminal-live">● live</span>
        </div>
        <div className="terminal-body">
          {done.map((line, i) => (
            <TerminalLine key={i} line={line} />
          ))}
          {!finished && (
            <p className="tone-cmd">
              {current}
              <span className="cursor" />
            </p>
          )}
          {finished && (
            <p className="tone-cmd">
              <span className="cyan">$</span> <span className="cursor" />
            </p>
          )}
        </div>
      </motion.div>
    </div>
  );
}

export function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const terminalY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0.15]);

  return (
    <section className="hero page-width" ref={ref} data-testid="hero-section">
      <motion.div className="hero-grid" style={{ opacity: fade }}>
        <div className="hero-copy">
          <motion.p
            className="eyebrow"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1, ease: EASE }}
          >
            <span className="status-dot" /> Available for select projects · India / Remote
          </motion.p>
          <h1 data-testid="hero-headline">
            {HERO_LINES.map((line, i) => (
              <span className="mask-line" key={i}>
                <motion.span
                  initial={{ y: "115%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 1.05, delay: 0.3 + i * 0.14, ease: EASE }}
                >
                  {line.text}
                  {line.em && <em>{line.em}</em>}
                  {line.tail}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            className="hero-subtitle"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
          >
            Forward Deployed Engineer and product-minded builder. I embed with ambitious teams, find
            the leverage, and ship the thing that makes the business move.
          </motion.p>
          <motion.div
            className="hero-actions"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1, ease: EASE }}
          >
            <Magnetic>
              <a className="button" href={CALENDAR} target="_blank" rel="noreferrer" data-testid="hero-book-call-button">
                Let’s talk about your problem <ArrowUpRight size={17} />
              </a>
            </Magnetic>
            <a className="text-link underline-link" href="#work" data-testid="hero-work-link">
              See the work <ChevronRight size={16} />
            </a>
          </motion.div>
          <motion.div
            className="proof-line"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.25 }}
          >
            <span>Trusted across</span>
            <b>AI · fintech · consumer finance · SaaS</b>
          </motion.div>
        </div>
        <motion.div style={{ y: terminalY }}>
          <TerminalCard />
        </motion.div>
      </motion.div>
    </section>
  );
}
